<div class="d-flex justify-content-between align-items-center mb-3">
    <h3 class="mb-0">Purchase Orders</h3>
    @if (canEdit) {
        <div class="d-flex gap-2">
            <button class="btn btn-outline-secondary btn-sm" (click)="triggerExtract()" [disabled]="isProcessingAi">
                @if (isProcessingAi) {
                    <span class="spinner-border spinner-border-sm me-1"></span>
                }
                Add from Receipt (AI)
            </button>
            <button class="btn btn-primary btn-sm" (click)="startNew()">+ New Purchase Order</button>
        </div>
    }
</div>

<input #extractInput type="file" class="d-none" accept=".pdf,.png,.jpg,.jpeg,.docx,.xlsx" multiple (change)="onExtractFileSelected($event)" />
<input #docInput type="file" class="d-none" multiple (change)="onDocFileSelected($event)" />

<div class="row g-3">
    <div class="col-lg-5">
        <div class="purchasing-table-panel">
                <table class="table table-sm table-hover mb-0 po-list">
                    <thead>
                        <tr>
                            <th>PO #</th>
                            <th>Vendor</th>
                            <th>Equipment</th>
                            <th>WO</th>
                            <th>Status</th>
                            <th class="text-end">Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        @for (order of orders; track order.id) {
                            <tr (click)="selectOrder(order)" [class.table-active]="selectedOrder?.id === order.id" style="cursor: pointer">
                                <td>{{ order.poNumber }}</td>
                                <td>{{ order.vendorName }}</td>
                                <td>{{ order.equipment }}</td>
                                <td>{{ order.workOrder }}</td>
                                <td><span class="badge" [ngClass]="statusBadgeClass(order.status)">{{ order.status }}</span></td>
                                <td class="text-end">{{ order.total | currency }}</td>
                            </tr>
                        }
                        @if (orders.length === 0) {
                            <tr>
                                <td colspan="6" class="text-center text-muted py-3">No purchase orders yet.</td>
                            </tr>
                        }
                    </tbody>
                </table>
        </div>
    </div>

    <div class="col-lg-7">
        @if (editing && editModel) {
            <div class="card">
                <div class="card-header d-flex justify-content-between align-items-center">
                    <span>{{ creating ? 'New Purchase Order' : 'Edit Purchase Order' }}</span>
                    @if (canEdit) {
                        <button class="btn btn-outline-secondary btn-sm" (click)="triggerExtract()" [disabled]="isProcessingAi">
                            @if (isProcessingAi) {
                                <span class="spinner-border spinner-border-sm me-1"></span>
                            }
                            Add from Receipt (AI)
                        </button>
                    }
                </div>
                <div class="card-body">
                    @if (extractWarning) {
                        <div class="alert alert-warning py-2 small">{{ extractWarning }}</div>
                    }
                    <div class="row g-2 mb-3">
                        <div class="col-md-4">
                            <label class="form-label small mb-0">PO Number (Vista Keystyle)</label>
                            <input class="form-control form-control-sm" [(ngModel)]="editModel.poNumber" placeholder="Enter Vista PO number" />
                        </div>
                        <div class="col-md-4">
                            <label class="form-label small mb-0">Status</label>
                            <select class="form-select form-select-sm" [(ngModel)]="editModel.status">
                                @for (s of statuses; track s) {
                                    <option [value]="s">{{ s }}</option>
                                }
                            </select>
                        </div>
                        <div class="col-md-4">
                            <label class="form-label small mb-0">Vendor</label>
                            <select class="form-select form-select-sm" [(ngModel)]="editModel.vendor" (ngModelChange)="onVendorChange()">
                                <option [ngValue]="undefined">-- Select vendor --</option>
                                @for (v of vendors; track v.vendorId) {
                                    <option [ngValue]="v.vendorId">{{ v.vendorName }}</option>
                                }
                            </select>
                        </div>
                        <div class="col-md-6">
                            <label class="form-label small mb-0">Equipment</label>
                            <input class="form-control form-control-sm" list="equipmentList" [(ngModel)]="editModel.equipment" (ngModelChange)="onEquipmentChange()" placeholder="Select equipment" />
                            <datalist id="equipmentList">
                                @for (eq of equipmentOptions; track eq.equipment) {
                                    <option [value]="eq.equipment">{{ eq.equipment }} - {{ eq.description }}</option>
                                }
                            </datalist>
                        </div>
                        <div class="col-md-6">
                            <label class="form-label small mb-0">Work Order</label>
                            <div class="d-flex gap-1">
                                <select class="form-select form-select-sm" [(ngModel)]="selectedWorkOrderKey" (ngModelChange)="onWorkOrderChange()" [disabled]="!editModel.equipment">
                                    <option value="">-- Select work order --</option>
                                    @for (wo of workOrders; track workOrderKey(wo)) {
                                        <option [value]="workOrderKey(wo)">{{ wo.source }} {{ wo.workOrder }} — {{ wo.description }}</option>
                                    }
                                </select>
                                <button type="button" class="btn btn-outline-secondary btn-sm text-nowrap" (click)="openCreateWo()" [disabled]="!editModel.equipment">+ WO</button>
                            </div>
                        </div>
                        <div class="col-md-4">
                            <label class="form-label small mb-0">WO Item</label>
                            <select class="form-select form-select-sm" [(ngModel)]="editModel.workOrderItem" (ngModelChange)="onHeaderWorkOrderItemChange()" [disabled]="workOrderItems.length === 0">
                                <option [ngValue]="undefined">-- Select item --</option>
                                @for (item of workOrderItems; track item.item) {
                                    <option [ngValue]="item.item">{{ item.item }} — {{ item.description }}</option>
                                }
                            </select>
                        </div>
                        <div class="col-md-4">
                            <label class="form-label small mb-0">Order Date</label>
                            <input type="date" class="form-control form-control-sm" [(ngModel)]="editModel.orderDate" />
                        </div>
                        <div class="col-md-4">
                            <label class="form-label small mb-0">Required By</label>
                            <input type="date" class="form-control form-control-sm" [(ngModel)]="editModel.requiredByDate" />
                        </div>
                        <div class="col-12">
                            <label class="form-label small mb-0">Description</label>
                            <textarea class="form-control form-control-sm" rows="2" [(ngModel)]="editModel.description"></textarea>
                        </div>
                    </div>

                    @if (showCreateWo) {
                        <div class="border rounded p-2 mb-3 bg-light">
                            <div class="small fw-semibold mb-2">Create WebApps work order</div>
                            <div class="row g-2">
                                <div class="col-md-6">
                                    <label class="form-label small mb-0">Description</label>
                                    <input class="form-control form-control-sm" [(ngModel)]="createWoModel.description" />
                                </div>
                                <div class="col-md-6">
                                    <label class="form-label small mb-0">System Affected</label>
                                    <select class="form-select form-select-sm" [(ngModel)]="createWoModel.systemAffected">
                                        <option value="">-- Select --</option>
                                        @for (cat of skillCategories; track cat.code) {
                                            <option [value]="cat.code">{{ cat.name }}</option>
                                        }
                                    </select>
                                </div>
                                <div class="col-12">
                                    <label class="form-label small mb-0">Notes</label>
                                    <input class="form-control form-control-sm" [(ngModel)]="createWoModel.notes" />
                                </div>
                                <div class="col-12 d-flex justify-content-end gap-2">
                                    <button type="button" class="btn btn-outline-secondary btn-sm" (click)="showCreateWo = false">Cancel</button>
                                    <button type="button" class="btn btn-primary btn-sm" (click)="createWorkOrder()" [disabled]="isCreatingWo">
                                        @if (isCreatingWo) {
                                            <span class="spinner-border spinner-border-sm me-1"></span>
                                        }
                                        Create Work Order
                                    </button>
                                </div>
                            </div>
                        </div>
                    }

                    <h6>Line Items</h6>
                    <div class="table-responsive">
                        <table class="table table-sm align-middle line-item-table">
                            <thead>
                                <tr>
                                    <th style="width: 40px">#</th>
                                    <th style="width: 100px">Item</th>
                                    <th>Description</th>
                                    <th style="width: 60px">UOM</th>
                                    <th style="width: 70px">Qty</th>
                                    <th style="width: 80px">Unit</th>
                                    <th style="width: 80px">Total</th>
                                    <th style="width: 200px">WO Item</th>
                                    <th style="width: 70px">CC</th>
                                    <th style="width: 140px">EM CT</th>
                                    <th style="width: 30px"></th>
                                </tr>
                            </thead>
                            <tbody>
                                @for (line of editModel.lineItems; track $index) {
                                    <tr>
                                        <td>{{ $index + 1 }}</td>
                                        <td><input class="form-control form-control-sm" [(ngModel)]="line.itemCode" /></td>
                                        <td><input class="form-control form-control-sm" [(ngModel)]="line.description" /></td>
                                        <td><input class="form-control form-control-sm" [(ngModel)]="line.uom" /></td>
                                        <td>
                                            <input type="number" class="form-control form-control-sm text-end" [(ngModel)]="line.quantity" (ngModelChange)="recalcLine(line)" />
                                        </td>
                                        <td>
                                            <input type="number" class="form-control form-control-sm text-end" [(ngModel)]="line.unitPrice" (ngModelChange)="recalcLine(line)" />
                                        </td>
                                        <td class="text-end">{{ line.lineTotal | currency }}</td>
                                        <td>
                                            <select
                                                class="form-select form-select-sm"
                                                [(ngModel)]="line.workOrderItem"
                                                [disabled]="workOrderItems.length === 0"
                                            >
                                                <option [ngValue]="undefined">-- Select --</option>
                                                @for (item of workOrderItems; track item.item) {
                                                    <option [ngValue]="item.item">{{ item.item }} — {{ item.description }}</option>
                                                }
                                                @if (isUnlistedWorkOrderItem(line.workOrderItem)) {
                                                    <option [ngValue]="line.workOrderItem">{{ line.workOrderItem }}</option>
                                                }
                                            </select>
                                        </td>
                                        <td><input class="form-control form-control-sm" [(ngModel)]="line.costCode" /></td>
                                        <td>
                                            <select class="form-select form-select-sm" [(ngModel)]="line.emCostType">
                                                <option [ngValue]="undefined">—</option>
                                                @for (ct of emCostTypes; track ct.costType) {
                                                    <option [ngValue]="ct.costType">{{ ct.costType }} - {{ ct.description }}</option>
                                                }
                                            </select>
                                        </td>
                                        <td><button class="btn btn-sm btn-outline-danger py-0 px-1" (click)="removeLine($index)">&times;</button></td>
                                    </tr>
                                }
                            </tbody>
                        </table>
                    </div>
                    <button class="btn btn-sm btn-outline-secondary mb-3" (click)="addLine()">+ Add Line</button>

                    <div class="row g-2 justify-content-end">
                        <div class="col-md-3">
                            <label class="form-label small mb-0">Tax</label>
                            <input type="number" class="form-control form-control-sm text-end" [(ngModel)]="editModel.taxAmount" (ngModelChange)="recalcTotals()" />
                        </div>
                        <div class="col-md-3">
                            <label class="form-label small mb-0">Freight</label>
                            <input type="number" class="form-control form-control-sm text-end" [(ngModel)]="editModel.freightAmount" (ngModelChange)="recalcTotals()" />
                        </div>
                        <div class="col-md-3">
                            <label class="form-label small mb-0">Total</label>
                            <input class="form-control form-control-sm text-end fw-bold" [value]="editModel.total | currency" disabled />
                        </div>
                    </div>
                </div>
                <div class="card-footer d-flex justify-content-end gap-2">
                    <button class="btn btn-outline-secondary btn-sm" (click)="cancelEdit()">Cancel</button>
                    <button class="btn btn-primary btn-sm" (click)="save()" [disabled]="isSaving">
                        @if (isSaving) {
                            <span class="spinner-border spinner-border-sm me-1"></span>
                        }
                        Save
                    </button>
                </div>
            </div>
        } @else if (selectedOrder) {
            <div class="card">
                <div class="card-header d-flex justify-content-between align-items-center">
                    <span>
                        {{ selectedOrder.poNumber }}
                        <span class="badge ms-2" [ngClass]="statusBadgeClass(selectedOrder.status)">{{ selectedOrder.status }}</span>
                    </span>
                    @if (canEdit) {
                        <div class="d-flex gap-2">
                            <button class="btn btn-outline-primary btn-sm" (click)="startEdit()">Edit</button>
                            <button class="btn btn-outline-secondary btn-sm" (click)="triggerExtract()" [disabled]="isProcessingAi">
                                @if (isProcessingAi) {
                                    <span class="spinner-border spinner-border-sm me-1"></span>
                                }
                                Add from Receipt (AI)
                            </button>
                            @if (canReceive) {
                                <button class="btn btn-outline-success btn-sm" (click)="navigateToReceive()">Receive</button>
                            }
                            <button class="btn btn-outline-success btn-sm" (click)="openEmail()">Email Vendor</button>
                            <button class="btn btn-outline-secondary btn-sm" (click)="triggerDocUpload()">Upload Doc</button>
                            <button class="btn btn-outline-danger btn-sm" (click)="deleteOrder(selectedOrder)">Archive</button>
                        </div>
                    }
                </div>
                <div class="card-body">
                    <dl class="row mb-2 small">
                        <dt class="col-sm-3">Vendor</dt>
                        <dd class="col-sm-9">{{ selectedOrder.vendorName || '—' }}</dd>
                        <dt class="col-sm-3">Equipment</dt>
                        <dd class="col-sm-9">{{ equipmentDisplay(selectedOrder.equipment) }}</dd>
                        <dt class="col-sm-3">Work Order</dt>
                        <dd class="col-sm-9">{{ selectedOrder.workOrder || '—' }}@if (selectedOrder.workOrderItem) { <span> / Item {{ selectedOrder.workOrderItem }}</span> }</dd>
                        <dt class="col-sm-3">Order Date</dt>
                        <dd class="col-sm-9">{{ (selectedOrder.orderDate | date: 'shortDate') || '—' }}</dd>
                        <dt class="col-sm-3">Required By</dt>
                        <dd class="col-sm-9">{{ (selectedOrder.requiredByDate | date: 'shortDate') || '—' }}</dd>
                        <dt class="col-sm-3">Description</dt>
                        <dd class="col-sm-9">{{ selectedOrder.description || '—' }}</dd>
                    </dl>

                    <h6>Line Items</h6>
                    <div class="table-responsive">
                        <table class="table table-sm mb-3">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Item</th>
                                    <th>Description</th>
                                    <th>UOM</th>
                                    <th class="text-end">Qty</th>
                                    <th class="text-end">Received</th>
                                    <th class="text-end">Invoiced</th>
                                    <th class="text-end">Unit</th>
                                    <th class="text-end">Total</th>
                                    <th>WO Item</th>
                                    <th>CC</th>
                                    <th>EM CT</th>
                                </tr>
                            </thead>
                            <tbody>
                                @for (line of selectedOrder.lineItems; track line.id) {
                                    <tr [ngClass]="lineReceiptClass(line)">
                                        <td>{{ line.lineNumber }}</td>
                                        <td>{{ line.itemCode }}</td>
                                        <td>{{ line.description }}</td>
                                        <td>{{ line.uom }}</td>
                                        <td class="text-end">{{ line.quantity }}</td>
                                        <td class="text-end">{{ line.quantityReceived }}</td>
                                        <td class="text-end">{{ line.quantityInvoiced }}</td>
                                        <td class="text-end">{{ line.unitPrice | currency }}</td>
                                        <td class="text-end">{{ line.lineTotal | currency }}</td>
                                        <td>{{ line.workOrderItem }}</td>
                                        <td>{{ line.costCode }}</td>
                                        <td>{{ emCostTypeDisplay(line.emCostType) }}</td>
                                    </tr>
                                }
                            </tbody>
                        </table>
                    </div>

                    <div class="d-flex justify-content-end small mb-3">
                        <div>
                            <div>Subtotal: {{ selectedOrder.subtotal | currency }}</div>
                            <div>Tax: {{ selectedOrder.taxAmount | currency }}</div>
                            <div>Freight: {{ selectedOrder.freightAmount | currency }}</div>
                            <div class="fw-bold">Total: {{ selectedOrder.total | currency }}</div>
                        </div>
                    </div>

                    <div class="d-flex gap-3 small text-muted mb-3">
                        <span><i class="bi bi-box-seam"></i> {{ selectedOrder.receiptCount }} receipt(s)</span>
                        <span>
                            <i class="bi bi-receipt"></i>
                            @if (selectedOrder.invoiceCount > 0) {
                                <a [routerLink]="['/purchasing/invoicing']" [queryParams]="{ orderId: selectedOrder.id }">{{ selectedOrder.invoiceCount }} invoice(s)</a>
                            } @else {
                                {{ selectedOrder.invoiceCount }} invoice(s)
                            }
                        </span>
                    </div>

                    @if (selectedOrder.documents && selectedOrder.documents.length > 0) {
                        <h6>Documents</h6>
                        <ul class="list-unstyled small">
                            @for (doc of selectedOrder.documents; track doc.id) {
                                <li>
                                    <a [href]="documentUrl(doc)" target="_blank">{{ doc.fileName }}</a>
                                    <span class="text-muted">({{ doc.documentType }})</span>
                                </li>
                            }
                        </ul>
                    }

                    <h6 class="mt-3">Receiving History</h6>
                    <div class="table-responsive">
                        <table class="table table-sm mb-0">
                            <thead>
                                <tr>
                                    <th>Receipt #</th>
                                    <th>Date</th>
                                    <th>Status</th>
                                    <th class="text-end">Lines</th>
                                </tr>
                            </thead>
                            <tbody>
                                @for (receipt of orderReceipts; track receipt.id) {
                                    <tr>
                                        <td>
                                            <a [routerLink]="['/purchasing/receiving']" [queryParams]="{ orderId: selectedOrder.id }">{{ receipt.receiptNumber }}</a>
                                        </td>
                                        <td>{{ receipt.receivedDate | date: 'shortDate' }}</td>
                                        <td><span class="badge" [ngClass]="receiptStatusBadgeClass(receipt.status)">{{ receipt.status }}</span></td>
                                        <td class="text-end">{{ receipt.lineItems.length || 0 }}</td>
                                    </tr>
                                }
                                @if (orderReceipts.length === 0) {
                                    <tr>
                                        <td colspan="4" class="text-center text-muted py-2">No receipts yet.</td>
                                    </tr>
                                }
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        } @else {
            <div class="text-muted p-4 text-center border rounded">Select a purchase order to view details, or create a new one. Use Add from Receipt (AI) to upload a cart, quote, or invoice.</div>
        }
    </div>
</div>

@if (emailing && selectedOrder) {
    <div class="modal-backdrop-custom" (click)="emailing = false"></div>
    <div class="modal-dialog-custom">
        <div class="card">
            <div class="card-header">Email PO {{ selectedOrder.poNumber }} to Vendor</div>
            <div class="card-body">
                <div class="mb-2">
                    <label class="form-label small mb-0">To Email</label>
                    <input class="form-control form-control-sm" [(ngModel)]="emailModel.toEmail" />
                </div>
                <div class="mb-2">
                    <label class="form-label small mb-0">CC Email</label>
                    <input class="form-control form-control-sm" [(ngModel)]="emailModel.ccEmail" />
                </div>
                <div class="mb-2">
                    <label class="form-label small mb-0">Subject</label>
                    <input class="form-control form-control-sm" [(ngModel)]="emailModel.subject" />
                </div>
                <div class="mb-2">
                    <label class="form-label small mb-0">Message</label>
                    <textarea class="form-control form-control-sm" rows="3" [(ngModel)]="emailModel.message"></textarea>
                </div>
                <p class="text-muted small mb-0">A PDF copy of the purchase order will be attached automatically.</p>
            </div>
            <div class="card-footer d-flex justify-content-end gap-2">
                <button class="btn btn-outline-secondary btn-sm" (click)="emailing = false">Cancel</button>
                <button class="btn btn-primary btn-sm" (click)="sendEmail()" [disabled]="!emailModel.toEmail">Send</button>
            </div>
        </div>
    </div>
}
